import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FaFacebookF, FaLinkedin, FaInstagram, FaYoutube, FaWhatsapp } from "react-icons/fa";
import { AiFillGithub, AiOutlineTwitter } from "react-icons/ai";
import { useSelector, useDispatch } from "react-redux";
import { AiFillShopping, AiFillHeart } from "react-icons/ai";
import { get_support_info } from "../store/reducers/authReducer";
import { GrMail } from "react-icons/gr";
import { IoIosCall } from "react-icons/io";
import { MdLocationOn } from "react-icons/md";
import api from "../api/api";

const Footer = ({ sellerId, sellerName }) => {
  const { card_product_count, wishlist_count } = useSelector((state) => state.card);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userInfo, supportInfo, seller } = useSelector((state) => state.auth);
  const { tabContent, status } = useSelector((state) => state.content);
  const { sellerName: currentSellerName } = useParams();
  
  // Get sellerId from localStorage/sessionStorage as fallback
  const storedSellerId = localStorage.getItem('sellerId') || sessionStorage.getItem('sellerId');
  const [socialMedia, setSocialMedia] = useState({
    facebook: '',
    twitter: '',
    linkedin: '',
    github: '',
    instagram: '',
    youtube: '',
    whatsapp: ''
  });
  const [linksContent, setLinksContent] = useState({});

  // Move these function declarations above useEffect
  const fetchSocialMedia = async () => {
    try {
      if (!seller?._id && !storedSellerId && !sellerName) {
        return;
      }
      
      // Try to use seller ID if available, otherwise use seller name
      const sellerIdentifier = seller?._id || storedSellerId || sellerName;
      const { data } = await api.get(`/social-media/seller/${sellerIdentifier}`);
      if (data.socialMedia) {
        setSocialMedia(data.socialMedia);
      }
    } catch (error) {
      // Error handling
    }
  };

  const fetchLinksContent = async () => {
    try {
      if (!seller?._id && !storedSellerId && !sellerName) {
        return;
      }
      
      // Try to use seller ID if available, otherwise use seller name
      const sellerIdentifier = seller?._id || storedSellerId || sellerName;
      const { data } = await api.get(`/links-content/${sellerIdentifier}`);
      if (data) {
        setLinksContent(data);
      }
    } catch (error) {
      // Error handling
    }
  };

  useEffect(() => {
    if (sellerId || sellerName) {
      dispatch(get_support_info(sellerId || sellerName));
    }
  }, [dispatch, sellerId, sellerName]);
  useEffect(() => {
    if (seller?._id || storedSellerId || sellerName) {
      fetchSocialMedia();
      fetchLinksContent();
    }
  }, [seller?._id, storedSellerId, sellerName]);
  if (!sellerName) {
    console.warn('Footer: sellerName is undefined');
    return null; // or return <div>Loading...</div>;
  }
  
  // Use sellerName for all navigation URLs
  const currentSellerNameForLinks = sellerName || sellerId;
  // Use sellerId for API calls
  const currentSellerId = sellerId || sellerName;

  const usefulLinks = [
    { name: "About our Shop", url: "about-our-shop" },
    { name: "Delivery Information", url: "delivery-information" },
    { name: "Shipping Policy", url: "shipping-policy" },
    { name: "Return Policy", url: "return-policy" },
    { name: "Blogs", url: "blogs" },
  ];

  const socialMediaPlatforms = [
    { name: 'facebook', icon: <FaFacebookF />, color: 'hover:bg-blue-600' },
    { name: 'twitter', icon: <AiOutlineTwitter />, color: 'hover:bg-sky-500' },
    { name: 'linkedin', icon: <FaLinkedin />, color: 'hover:bg-blue-700' },
    { name: 'github', icon: <AiFillGithub />, color: 'hover:bg-gray-800' },
    { name: 'instagram', icon: <FaInstagram />, color: 'hover:bg-pink-600' },
    { name: 'youtube', icon: <FaYoutube />, color: 'hover:bg-red-600' },
    { name: 'whatsapp', icon: <FaWhatsapp />, color: 'hover:bg-green-500' }
  ];

  // Format phone number for WhatsApp
  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    // Remove any non-digit characters
    const cleaned = phone.replace(/\D/g, '');
    
    // Get country code from seller info, default to '27' for South Africa if not available
    const countryCode = seller?.countryCode || '27';
    
    // If number starts with 0, replace with country code
    if (cleaned.startsWith('0')) {
      return countryCode + cleaned.substring(1);
    }
    // If number doesn't start with country code, add it
    if (!cleaned.startsWith(countryCode)) {
      return countryCode + cleaned;
    }
    return cleaned;
  };

  return (
    <footer className="bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Main Footer Content */}
      <div className="w-[85%] mx-auto py-8">
        <div className="flex flex-wrap justify-between gap-8">
          {/* Company Info & Contact */}
          <div className="flex-1 min-w-[250px]">
            <Link to={`/${currentSellerNameForLinks}`} className="block mb-4">
              <div className="w-[180px] h-[60px] relative">
                <img
                  src={seller?.image || "/images/logo.png"}
                  alt={seller?.shopInfo?.shopName || "Store Logo"}
                  className="w-full h-full object-contain object-center"
                  onError={(e) => {
                    // Hide the image if it fails to load
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            </Link>
            
          </div>

          {/* Quick Links */}
          <div className="flex-1 min-w-[200px]">
            <div className="mb-6">
              <h3 className="text-base font-semibold text-gray-800 mb-2">Quick Links</h3>
              <div className="w-12 h-1 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full"></div>
            </div>
            <ul className="space-y-2">
              {usefulLinks.map((link, index) => (
                <li key={index}>
                  <Link 
                    to={`/${currentSellerNameForLinks}/page/${link.url}`}
                    className="text-sm text-gray-600 hover:text-indigo-600 transition-colors duration-300 flex items-center gap-2 group"
                  >
                    <span className="w-1 h-1 bg-indigo-200 rounded-full group-hover:bg-indigo-600 transition-colors duration-300"></span>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Info */}
          <div className="flex-1 min-w-[200px]">
            <div className="mb-6">
              <h3 className="text-base font-semibold text-gray-800 mb-2">Support Info</h3>
              <div className="w-12 h-1 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full"></div>
            </div>
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2 text-gray-600 hover:text-indigo-600 transition-colors duration-300 group">
                <div className="w-7 h-7 rounded-full bg-indigo-50 flex items-center justify-center group-hover:bg-indigo-100 transition-colors duration-300">
                  <GrMail className="text-indigo-600 text-sm" />
                </div>
                <span className="text-sm">{seller?.support?.email || "Contact us"}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600 hover:text-indigo-600 transition-colors duration-300 group">
                <div className="w-7 h-7 rounded-full bg-indigo-50 flex items-center justify-center group-hover:bg-indigo-100 transition-colors duration-300">
                  <IoIosCall className="text-indigo-600 text-sm" />
                </div>
                <span className="text-sm">{seller?.support?.mobileNo || "Contact us"}</span>
              </div>
              {seller?.support?.supportHours && (
                <div className="flex items-center gap-2 text-gray-600 hover:text-indigo-600 transition-colors duration-300 group">
                  <div className="w-7 h-7 rounded-full bg-indigo-50 flex items-center justify-center group-hover:bg-indigo-100 transition-colors duration-300">
                    <IoIosCall className="text-indigo-600 text-sm" />
                  </div>
                  <span className="text-sm">Support Hours: {seller.support.supportHours}</span>
                </div>
              )}
              {seller?.support?.address && (
                <div className="flex items-center gap-2 text-gray-600 hover:text-indigo-600 transition-colors duration-300 group">
                  <div className="w-7 h-7 rounded-full bg-indigo-50 flex items-center justify-center group-hover:bg-indigo-100 transition-colors duration-300">
                    <MdLocationOn className="text-indigo-600 text-sm" />
                  </div>
                  <span className="text-sm">{seller.support.address}</span>
                </div>
              )}
            </div>
          </div>

          {/* Newsletter & Social */}
          <div className="flex-1 min-w-[250px]">
            <div className="flex flex-col gap-6">
              <div>
                <div className="mb-6">
                  <h3 className="text-base font-semibold text-gray-800 mb-2">Newsletter</h3>
                  <div className="w-12 h-1 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full"></div>
                </div>
                <div className="relative">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-md focus:outline-none focus:border-indigo-500 text-sm shadow-sm"
                  />
                  <button className="absolute right-1 top-1/2 -translate-y-1/2 px-3 py-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded text-sm hover:from-indigo-700 hover:to-purple-700 transition-all duration-300">
                    Subscribe
                  </button>
                </div>
              </div>
              <div>
                <div className="mb-6">
                  <h3 className="text-base font-semibold text-gray-800 mb-2">Follow Us</h3>
                  <div className="w-12 h-1 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full"></div>
                </div>
                <div className="flex items-center gap-2">
                  {socialMediaPlatforms.map((platform) => {
                    // console.log(`Checking ${platform.name}:`, socialMedia[platform.name]); // Debug log
                    return socialMedia[platform.name] && (
                      <a
                        key={platform.name}
                        href={platform.name === 'whatsapp' 
                          ? `https://wa.me/${formatPhoneNumber(socialMedia[platform.name])}`
                          : socialMedia[platform.name]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`w-7 h-7 flex items-center justify-center rounded-full bg-white ${platform.color} shadow-sm`}
                      >
                        {platform.icon}
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-200">
        <div className="w-[85%] mx-auto py-4">
          <div className="flex flex-wrap justify-between items-center gap-4">
            <p className="text-sm text-gray-600">
              Copyright ©2025 4POS. All rights reserved | A Proud 4POS Development
            </p>
            <div className="flex items-center gap-4">
              <Link to={`/${currentSellerNameForLinks}/page/privacy-policy`} className="text-sm text-gray-600 hover:text-indigo-600 transition-colors duration-300">
                Privacy
              </Link>
              <Link to={`/${currentSellerNameForLinks}/page/terms-and-conditions`} className="text-sm text-gray-600 hover:text-indigo-600 transition-colors duration-300">
                Terms
              </Link>
              <Link to={`/${currentSellerNameForLinks}/contact`} className="text-sm text-gray-600 hover:text-indigo-600 transition-colors duration-300">
                Contact
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Action Buttons */}
      <div className="hidden fixed md-lg:block w-[50px] bottom-3 h-[110px] right-2 bg-white rounded-full p-2 shadow-lg hover:shadow-xl transition-shadow duration-300">
        <div className="w-full h-full flex gap-3 flex-col justify-center items-center">
          <div
            onClick={() => navigate(userInfo ? `/${currentSellerNameForLinks}/card` : `/${currentSellerNameForLinks}/card`)}
            className="relative flex justify-center items-center cursor-pointer w-[35px] h-[35px] rounded-full bg-gradient-to-r from-indigo-50 to-purple-50 hover:from-indigo-100 hover:to-purple-100 transition-all duration-300"
          >
            <span className="text-xl text-indigo-600">
              <AiFillShopping />
            </span>
            {card_product_count !== 0 && (
              <div className="w-[20px] h-[20px] absolute bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full text-white flex justify-center items-center -top-[3px] -right-[5px] shadow-md">
                {card_product_count}
              </div>
            )}
          </div>
          <div
            onClick={() => navigate(userInfo ? `/${currentSellerNameForLinks}/dashboard/my-wishlist` : `/${currentSellerNameForLinks}/dashboard/my-wishlist`)}
            className="relative flex justify-center items-center cursor-pointer w-[35px] h-[35px] rounded-full bg-gradient-to-r from-indigo-50 to-purple-50 hover:from-indigo-100 hover:to-purple-100 transition-all duration-300"
          >
            <span className="text-xl text-indigo-600">
              <AiFillHeart />
            </span>
            {wishlist_count !== 0 && (
              <div className="w-[20px] h-[20px] absolute bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full text-white flex justify-center items-center -top-[3px] -right-[5px] shadow-md">
                {wishlist_count}
              </div>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
